import { useEffect, useState } from 'react';
import type { WeatherForecastResponse } from '@greeniq/shared';
import { getWeatherForecast } from '../api/weather.js';
import { DataCard } from './ui/data-card';
import { StatTile } from './ui/stat-tile';
import { Badge } from './ui/badge';

interface Props {
  courseSlug: string;
}

export function OnSiteStationCard({ courseSlug }: Props) {
  const [data, setData] = useState<WeatherForecastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getWeatherForecast(courseSlug);
        if (!cancelled) setData(resp);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  if (error) {
    return (
      <DataCard className="mt-6" title="On-site station">
        <p className="text-sm text-[color:var(--color-error)]">Weather unavailable: {error}</p>
      </DataCard>
    );
  }
  if (!data) {
    return (
      <DataCard className="mt-6" title="On-site station">
        <p className="text-sm text-fg-muted">Loading conditions…</p>
      </DataCard>
    );
  }

  const c = data.current;
  const today = data.daily[0];
  const asOf = c.asOf ? `As of ${new Date(c.asOf).toLocaleString()} · Open-Meteo` : 'Open-Meteo';

  return (
    <DataCard
      className="mt-6"
      title="On-site station"
      subtitle={asOf}
      actions={<Badge variant="warning">Synthetic microclimate</Badge>}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <StatTile label="Temp" value={fmtTemp(c.tempF)} detail="°F" />
        <StatTile label="Humidity" value={fmtPct(c.relativeHumidityPct)} detail="%" />
        <StatTile label="Wind" value={fmtDec(c.windSpeedMph, 1)} detail="mph" />
        <StatTile label="Last-hr rain" value={fmtDec(c.precipitationIn, 2)} detail="in" />
        <StatTile label="Soil 6cm" value={fmtTemp(c.soilTemp6cmF)} detail="°F" />
        <StatTile label="Cloud" value={fmtPct(c.cloudCoverPct)} detail="%" />
      </div>

      {today && (
        <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-4 text-sm md:grid-cols-4">
          <StatLine label="Today high" value={`${fmtTemp(today.tempMaxF)}°F`} />
          <StatLine label="Today low" value={`${fmtTemp(today.tempMinF)}°F`} />
          <StatLine
            label="Today rain"
            value={`${fmtDec(today.precipitationIn, 2)}″ · ${fmtPct(today.precipitationProbabilityPct)}%`}
          />
          <StatLine label="Today wind max" value={`${fmtDec(today.windSpeedMaxMph, 1)} mph`} />
        </div>
      )}
    </DataCard>
  );
}

function StatLine({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-fg-muted">
        {label}
      </div>
      <div className="mt-0.5 font-semibold text-fg">{value}</div>
    </div>
  );
}

function fmtTemp(v: number | null): string {
  return v === null ? '—' : Math.round(v).toString();
}
function fmtPct(v: number | null): string {
  return v === null ? '—' : Math.round(v).toString();
}
function fmtDec(v: number | null, places: number): string {
  return v === null ? '—' : v.toFixed(places);
}
